/* Dr Beans homepage. Progressive enhancement; Shopify owns cart and customer forms. */
(() => {
  'use strict';
  const root = document.getElementById('drbeans-home-concept') || document.body;
  if (!root) return;

  const returnFocus = new WeakMap();

  /* One readable announcement at a time, with a seamless last-to-first slide. */
  const announcementBars = new Map();
  function initAnnouncements(bar) {
    if (announcementBars.has(bar)) {
      announcementBars.get(bar).refresh();
      return;
    }
    const viewport = bar.querySelector('.dr-announcement-overflow');
    const motion = bar.querySelector('.dr-announcement-motion');
    const group = bar.querySelector('[data-dr-announcement-group]');
    if (!viewport || !motion || !group) return;
    const mobile = window.matchMedia('(max-width: 860px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animation = null;
    let disposed = false;
    let frame = 0;
    let lastWidth = -1;

    /* Keep the announcement cycle running through hover, tap and keyboard focus.
       Only pause while the browser tab itself is hidden. */
    function syncVisibilityPlayback() {
      if (!animation) return;
      if (document.hidden) animation.pause();
      else animation.play();
    }
    function reset() {
      animation?.cancel();
      animation = null;
      motion.querySelectorAll('[data-dr-announcement-copy]').forEach(copy => copy.remove());
      bar.classList.remove('dr-announcement-ready');
      bar.style.removeProperty('--dr-announcement-height');
    }
    function measure() {
      frame = 0;
      if (disposed) return;
      const previousTime = Number(animation?.currentTime) || 0;
      reset();
      const items = [...group.querySelectorAll('.dr-topbar-item')];
      const enabled = (mobile.matches ? bar.dataset.mobileScroll : bar.dataset.desktopScroll) === 'true';
      lastWidth = viewport.clientWidth;
      if (!enabled || reduced.matches || items.length < 2 || !lastWidth || typeof motion.animate !== 'function') return;
      const height = Math.ceil(Math.max(44, ...items.map(item => item.getBoundingClientRect().height)));
      if (!Number.isFinite(height)) return;
      bar.style.setProperty('--dr-announcement-height', height + 'px');
      const copy = group.cloneNode(true);
      copy.removeAttribute('data-dr-announcement-group');
      copy.setAttribute('data-dr-announcement-copy', '');
      copy.setAttribute('aria-hidden', 'true');
      [copy, ...copy.querySelectorAll('*')].forEach(node => {
        node.removeAttribute('id');
        for (const attribute of [...node.attributes]) if (attribute.name.startsWith('data-shopify')) node.removeAttribute(attribute.name);
      });
      copy.querySelectorAll('a,button,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
      motion.append(copy);
      const duration = Math.max(6000, (Number(bar.dataset.cycleSeconds) || 12) * 1000);
      const travel = Math.min(650 / duration, 0.2 / items.length);
      const frames = [{transform: 'translateY(0px)', offset: 0}];
      for (let i = 0; i < items.length; i++) {
        frames.push({transform: 'translateY(' + (-i * height) + 'px)', offset: (i + 1) / items.length - travel, easing: 'ease-in-out'});
        frames.push({transform: 'translateY(' + (-(i + 1) * height) + 'px)', offset: (i + 1) / items.length});
      }
      animation = motion.animate(frames, {
        duration, iterations: Infinity,
        direction: bar.dataset.direction === 'down' ? 'reverse' : 'normal'
      });
      animation.currentTime = previousTime % duration;
      bar.classList.add('dr-announcement-ready');
      syncVisibilityPlayback();
    }
    function refresh() {
      if (!disposed && !frame) frame = window.requestAnimationFrame(measure);
    }
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
      if (viewport.clientWidth !== lastWidth) refresh();
    }) : null;
    resize?.observe(viewport);
    const edits = typeof MutationObserver === 'function' ? new MutationObserver(refresh) : null;
    edits?.observe(group, {childList: true, subtree: true, characterData: true, attributes: true});
    const listen = (query, method) => {
      if (query[method + 'EventListener']) query[method + 'EventListener']('change', refresh);
      else query[method + 'Listener']?.(refresh);
    };
    listen(mobile, 'add'); listen(reduced, 'add');
    window.addEventListener('resize', refresh);
    document.addEventListener('visibilitychange', syncVisibilityPlayback);
    document.fonts?.ready.then(refresh);
    document.fonts?.addEventListener('loadingdone', refresh);
    announcementBars.set(bar, {
      refresh,
      destroy() {
        disposed = true;
        window.cancelAnimationFrame(frame);
        resize?.disconnect(); edits?.disconnect();
        listen(mobile, 'remove'); listen(reduced, 'remove');
        window.removeEventListener('resize', refresh);
        document.removeEventListener('visibilitychange', syncVisibilityPlayback);
        document.fonts?.removeEventListener('loadingdone', refresh);
        reset();
        announcementBars.delete(bar);
      }
    });
    measure();
  }

  /* Measure one natural-width cycle and fill the viewport plus a complete cycle.
     Moving by that exact cycle width makes the last frame match the first in RTL
     and LTR layouts, even with one short message or an ultrawide screen. */
  const marquees = new Map();
  function initMarquee(bar) {
    if (marquees.has(bar)) {
      marquees.get(bar).refresh();
      return;
    }
    const viewport = bar.querySelector('[data-dr-marquee-viewport]');
    const motion = bar.querySelector('[data-dr-marquee-motion]');
    const group = bar.querySelector('[data-dr-marquee-group]');
    if (!viewport || !motion || !group) return;
    const mobile = window.matchMedia('(max-width: 860px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let disposed = false;
    let lastWidth = 0;
    let lastViewport = 0;
    let lastContent = '';
    const removeCopies = () => motion.querySelectorAll('[data-dr-marquee-copy]').forEach(copy => copy.remove());

    function measure() {
      frame = 0;
      if (disposed) return;
      const enabled = bar.hasAttribute('data-dr-topbar')
        ? (mobile.matches ? bar.dataset.mobileScroll : bar.dataset.desktopScroll) === 'true'
        : bar.dataset.drAnimate !== 'false';
      const width = group.getBoundingClientRect().width;
      const available = viewport.clientWidth;
      if (!enabled || reduced.matches || !Number.isFinite(width) || width <= 0 || available <= 0) {
        bar.classList.remove('dr-marquee-ready');
        removeCopies();
        lastWidth = 0;
        return;
      }
      const content = group.innerHTML;
      if (width === lastWidth && available === lastViewport && content === lastContent) return;
      lastWidth = width;
      lastViewport = available;
      lastContent = content;
      removeCopies();
      /* One extra pixel keeps fractional layout rounding from exposing an edge. */
      const copies = Math.ceil((available + 1) / width);
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < copies; i++) {
        const copy = group.cloneNode(true);
        copy.removeAttribute('data-dr-marquee-group');
        copy.setAttribute('data-dr-marquee-copy', '');
        copy.setAttribute('aria-hidden', 'true');
        [copy, ...copy.querySelectorAll('*')].forEach(node => {
          node.removeAttribute('id');
          /* Shopify selection targets belong to the original message only. */
          for (const attribute of [...node.attributes]) {
            if (attribute.name.startsWith('data-shopify')) node.removeAttribute(attribute.name);
          }
        });
        copy.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
        fragment.append(copy);
      }
      motion.append(fragment);
      bar.style.setProperty('--dr-marquee-distance', -width + 'px');
      viewport.scrollLeft = 0;
      bar.classList.add('dr-marquee-ready');
    }

    function refresh() {
      if (disposed || frame) return;
      frame = window.requestAnimationFrame(measure);
    }
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(refresh) : null;
    resize?.observe(viewport);
    resize?.observe(group);
    /* Text/icon edits at the same width still need fresh copies. */
    const edits = typeof MutationObserver === 'function' ? new MutationObserver(refresh) : null;
    edits?.observe(group, {childList: true, subtree: true, characterData: true, attributes: true});
    const listen = (query, method) => {
      if (query[method + 'EventListener']) query[method + 'EventListener']('change', refresh);
      else query[method + 'Listener']?.(refresh);
    };
    listen(mobile, 'add');
    listen(reduced, 'add');
    window.addEventListener('resize', refresh);
    document.fonts?.ready.then(refresh);
    document.fonts?.addEventListener('loadingdone', refresh);
    marquees.set(bar, {
      refresh,
      destroy() {
        disposed = true;
        window.cancelAnimationFrame(frame);
        resize?.disconnect();
        edits?.disconnect();
        listen(mobile, 'remove');
        listen(reduced, 'remove');
        window.removeEventListener('resize', refresh);
        document.fonts?.removeEventListener('loadingdone', refresh);
        bar.classList.remove('dr-marquee-ready');
        removeCopies();
        marquees.delete(bar);
      }
    });
    measure();
  }

  function updateTaste(section, announce = true) {
    const roastButton = section.querySelector('[data-roast][aria-pressed="true"]');
    const blendButton = section.querySelector('[data-blend][aria-pressed="true"]');
    const roast = roastButton?.dataset.roast || 'وسط';
    const blend = blendButton?.dataset.blend || 'سادة';
    const key = roast + '|' + blend;
    let selected = null;
    section.querySelectorAll('[data-dr-recommendation]').forEach(panel => {
      const active = panel.dataset.drRecommendation === key && !selected;
      panel.hidden = !active;
      if (active) selected = panel;
    });
    const fallback = section.querySelector('[data-dr-missing]');
    if (fallback) fallback.hidden = !!selected;
    section.querySelector('[data-dr-roast-hint]').textContent = roastButton?.dataset.hint || '';
    section.querySelector('[data-dr-blend-hint]').textContent = blendButton?.dataset.hint || '';
    if (announce) {
      section.querySelector('[data-dr-announcement]').textContent =
        selected?.querySelector('[data-dr-rec-title]')?.textContent || 'شوف الاختيارات المتاحة من البن التركي';
    }
  }

  function init(scope) {
    scope.querySelectorAll('[data-dr-taste]').forEach(section => updateTaste(section, false));
    scope.querySelectorAll('[data-dr-marquee]').forEach(initMarquee);
    scope.querySelectorAll('[data-dr-announcements]').forEach(initAnnouncements);
  }
  init(root);

  /* Shared Dr Beans quick-view used by homepage and product recommendations. */
  const quickViews = new WeakMap();

  function normalizeQuickOption(value) {
    return String(value ?? '')
      .normalize('NFKC')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function sameQuickOption(a, b) {
    return normalizeQuickOption(a) === normalizeQuickOption(b);
  }

  function readQuickViewSelections(dialog) {
    return [...dialog.querySelectorAll('[data-dr-option-group]')].map(group =>
      group.querySelector('[data-dr-option-value][aria-pressed="true"]')?.dataset.drOptionValue || ''
    );
  }

  function variantMatchesSelections(variant, selections) {
    if (!variant?.options || !selections?.length) return false;
    return selections.every((value, index) => value !== '' && sameQuickOption(variant.options[index], value));
  }

  function getQuickView(dialog) {
    if (!dialog) return null;
    if (quickViews.has(dialog)) return quickViews.get(dialog);
    const json = dialog.querySelector('[data-dr-variants]');
    let variants = [];
    try { variants = JSON.parse(json?.textContent || '[]'); } catch (error) { console.error('Dr Beans quick-view variants:', error); }
    variants = Array.isArray(variants) ? variants.map(variant => ({
      ...variant,
      options: Array.isArray(variant.options) ? variant.options.map(value => String(value ?? '')) : []
    })) : [];

    let selected = readQuickViewSelections(dialog);
    let current = variants.find(variant => variantMatchesSelections(variant, selected)) || null;
    if (!current) current = variants.find(variant => variant.available) || variants[0] || null;
    if (current && (!selected.length || selected.some(value => !value))) selected = current.options.slice();

    const state = { variants, current, selected };
    quickViews.set(dialog, state);
    return state;
  }

  function setQuickViewMedia(dialog, mediaId) {
    if (!mediaId) return;
    const thumb = [...dialog.querySelectorAll('[data-dr-media-id]')].find(item => String(item.dataset.drMediaId) === String(mediaId));
    if (!thumb) return;
    const image = dialog.querySelector('[data-dr-main-image]');
    if (image && thumb.dataset.drMediaSrc) {
      image.src = thumb.dataset.drMediaSrc;
      image.alt = thumb.dataset.drMediaAlt || image.alt;
    }
    dialog.querySelectorAll('[data-dr-media-id]').forEach(item => {
      const active = item === thumb;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
  }

  function paintQuickViewSelections(dialog, selections) {
    if (!Array.isArray(selections)) return;
    dialog.querySelectorAll('[data-dr-option-group]').forEach(group => {
      const index = Number(group.dataset.drOptionIndex);
      const selectedValue = selections[index] ?? '';
      group.querySelectorAll('[data-dr-option-value]').forEach(button => {
        const selected = sameQuickOption(button.dataset.drOptionValue, selectedValue);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    });
  }

  function bestQuickViewVariant(state, changedIndex, changedValue) {
    if (!state) return null;

    /* Build the desired combination from the variant that is actually active,
       not from CSS/aria state. This keeps every click authoritative even when
       another script repaints the dialog between pointerdown and click. */
    const desired = (state.current?.options?.slice() || state.selected?.slice() || []);
    desired[changedIndex] = changedValue;

    const candidates = state.variants.filter(variant =>
      sameQuickOption(variant.options?.[changedIndex], changedValue)
    );
    if (!candidates.length) return null;

    const exactAvailable = candidates.find(variant =>
      variant.available && variantMatchesSelections(variant, desired)
    );
    if (exactAvailable) return exactAvailable;

    const exact = candidates.find(variant => variantMatchesSelections(variant, desired));
    if (exact) return exact;

    const available = candidates.filter(variant => variant.available);
    const pool = available.length ? available : candidates;
    return pool
      .map((variant, order) => ({
        variant,
        order,
        score: variant.options?.reduce((score, optionValue, optionIndex) =>
          score + (optionIndex !== changedIndex && desired[optionIndex] && sameQuickOption(optionValue, desired[optionIndex]) ? 1 : 0), 0) || 0
      }))
      .sort((a, b) => b.score - a.score || a.order - b.order)[0]?.variant || pool[0];
  }

  function clampQuickViewQuantity(input, variant) {
    if (!input || !variant) return;
    const min = Number(variant.min) || 1;
    const increment = Number(variant.increment) || 1;
    const max = variant.max == null ? null : Number(variant.max);
    let value = Number(input.value);
    if (!Number.isFinite(value)) value = min;
    value = Math.max(min, value);
    if (max != null && Number.isFinite(max)) value = Math.min(max, value);
    const steps = Math.max(0, Math.round((value - min) / increment));
    value = min + steps * increment;
    if (max != null && value > max) value = max;
    input.min = String(min);
    input.step = String(increment);
    if (max != null) input.max = String(max); else input.removeAttribute('max');
    input.value = String(value);
  }

  function syncQuickViewOptionAvailability(dialog, state) {
    dialog.querySelectorAll('[data-dr-option-group]').forEach(group => {
      const index = Number(group.dataset.drOptionIndex);
      group.querySelectorAll('[data-dr-option-value]').forEach(button => {
        const possible = state.variants.some(item =>
          item.available && sameQuickOption(item.options?.[index], button.dataset.drOptionValue)
        );
        /* Never make an option button dead. If there is no purchasable variant
           for the value, we show that visually and let the click explain it. */
        button.disabled = false;
        button.setAttribute('aria-disabled', String(!possible));
        button.classList.toggle('is-unavailable', !possible);
        button.dataset.drOptionAvailable = String(possible);
      });
    });
  }

  function applyQuickViewVariant(dialog, variant) {
    const state = getQuickView(dialog);
    if (!state) return;

    state.current = variant || null;
    if (variant?.options?.length) state.selected = variant.options.slice();
    paintQuickViewSelections(dialog, state.selected || []);
    syncQuickViewOptionAvailability(dialog, state);

    const idInput = dialog.querySelector('[data-dr-variant-id]');
    const price = dialog.querySelector('[data-dr-current-price]');
    const compare = dialog.querySelector('[data-dr-compare-price]');
    const sale = dialog.querySelector('[data-dr-sale-badge]');
    const add = dialog.querySelector('[data-dr-add-to-cart]');
    const addLabel = dialog.querySelector('[data-dr-add-label]');
    const qty = dialog.querySelector('[data-dr-qty-input]');
    const status = dialog.querySelector('[data-dr-qv-status]');
    const available = !!variant?.available;

    if (variant) {
      if (idInput) idInput.value = variant.id;
      if (price) price.textContent = variant.priceText || '';
      if (compare) {
        compare.textContent = variant.compareText || '';
        compare.hidden = !(variant.compareAtPrice > variant.price);
      }
      if (sale) {
        const discount = variant.compareAtPrice > variant.price
          ? Math.round(((variant.compareAtPrice - variant.price) / variant.compareAtPrice) * 100)
          : 0;
        sale.textContent = discount ? '-' + discount + '%' : '';
        sale.classList.toggle('is-hidden', !discount);
      }
      clampQuickViewQuantity(qty, variant);
      if (variant.mediaId) setQuickViewMedia(dialog, variant.mediaId);
      if (status) {
        status.textContent = available ? '' : 'الاختيار ده غير متاح حاليًا. جرّب اختيار تاني.';
        status.classList.toggle('is-error', !available);
      }
    } else {
      if (idInput) idInput.value = '';
      if (status) {
        status.textContent = 'التركيبة دي غير متاحة. اختار توليفة تانية.';
        status.classList.add('is-error');
      }
    }

    if (add) add.disabled = !available;
    if (addLabel) addLabel.textContent = available ? 'أضف إلى السلة' : 'غير متاح';
  }

  function selectQuickViewOption(dialog, optionButton) {
    const state = getQuickView(dialog);
    const group = optionButton?.closest('[data-dr-option-group]');
    if (!state || !group) return;

    const index = Number(group.dataset.drOptionIndex);
    const value = optionButton.dataset.drOptionValue || '';
    if (!Number.isInteger(index) || !value) return;

    /* Paint the pressed value immediately so the click always feels responsive,
       then resolve and paint the authoritative Shopify variant. */
    const optimistic = state.current?.options?.slice() || state.selected?.slice() || [];
    optimistic[index] = value;
    state.selected = optimistic;
    paintQuickViewSelections(dialog, optimistic);

    const variant = bestQuickViewVariant(state, index, value);
    if (variant) {
      applyQuickViewVariant(dialog, variant);
      return;
    }

    state.current = null;
    syncQuickViewOptionAvailability(dialog, state);
    const idInput = dialog.querySelector('[data-dr-variant-id]');
    const add = dialog.querySelector('[data-dr-add-to-cart]');
    const addLabel = dialog.querySelector('[data-dr-add-label]');
    const status = dialog.querySelector('[data-dr-qv-status]');
    if (idInput) idInput.value = '';
    if (add) add.disabled = true;
    if (addLabel) addLabel.textContent = 'غير متاح';
    if (status) {
      status.textContent = 'التركيبة دي غير متاحة. اختار توليفة تانية.';
      status.classList.add('is-error');
    }
  }

  function updateQuickView(dialog) {
    const state = getQuickView(dialog);
    if (!state) return;
    let variant = state.variants.find(item => variantMatchesSelections(item, state.selected)) || state.current;
    if (!variant) variant = state.variants.find(item => item.available) || state.variants[0] || null;
    applyQuickViewVariant(dialog, variant);
  }

  const boundQuickViews = new WeakSet();

  function bindQuickViewControls(dialog) {
    if (!dialog || boundQuickViews.has(dialog)) return;
    boundQuickViews.add(dialog);

    /* The live storefront has other theme/app listeners around product cards.
       Scope the capture handler to the dialog itself so the pressed option is
       resolved before any outer bubble listeners can repaint the default
       variant, without swallowing clicks globally. */
    const handleOptionActivation = event => {
      const button = event.target instanceof Element ? event.target.closest('[data-dr-option-value]') : null;
      if (!button || !dialog.contains(button)) return;
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      selectQuickViewOption(dialog, button);
      try { button.focus({ preventScroll: true }); } catch (_) { button.focus(); }
    };

    dialog.addEventListener('click', handleOptionActivation, true);
    dialog.addEventListener('keydown', handleOptionActivation, true);
  }

  function initQuickView(dialog) {
    if (!dialog?.matches('[data-dr-quickview]')) return;
    bindQuickViewControls(dialog);

    const state = getQuickView(dialog);
    if (!state) return;

    const domSelections = readQuickViewSelections(dialog);
    if (domSelections.length && domSelections.every(Boolean)) state.selected = domSelections;
    updateQuickView(dialog);
  }

  /* Initialize every rendered dialog while the DOM is stable. initQuickView is
     also called immediately before opening, so Shopify section re-renders and
     dynamically inserted product cards get the same direct bindings. */
  root.querySelectorAll('[data-dr-quickview]').forEach(initQuickView);

  async function addFromQuickView(form) {
    const dialog = form.closest('[data-dr-quickview]');
    const state = getQuickView(dialog);
    const variant = state?.current;
    const qtyInput = form.querySelector('[data-dr-qty-input]');
    const quantity = Math.max(1, Number(qtyInput?.value) || 1);
    const status = dialog?.querySelector('[data-dr-qv-status]');
    const addButton = dialog?.querySelector('[data-dr-add-to-cart]');
    const addLabel = dialog?.querySelector('[data-dr-add-label]');
    if (!variant?.available) return;

    if (status) { status.textContent = 'جارٍ الإضافة إلى السلة…'; status.classList.remove('is-error'); }
    if (addButton) addButton.disabled = true;
    if (addLabel) addLabel.textContent = 'جارٍ الإضافة…';

    try {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(shopRoot + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
        body: JSON.stringify({items: [{id: Number(variant.id), quantity}]})
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || payload.message || 'تعذر إضافة المنتج إلى السلة.');

      const cartResponse = await fetch(shopRoot + 'cart.js', {credentials: 'same-origin', headers: {'Accept': 'application/json'}});
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      if (cart) {
        root.querySelectorAll('.dr-cart-count, .cart-count').forEach(count => { count.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('dr:cart-updated', {detail: {cart, product: payload}}));
      }

      if (status) status.textContent = 'تمت الإضافة إلى السلة بنجاح ✓';
      if (addLabel) addLabel.textContent = 'تمت الإضافة ✓';
      window.setTimeout(() => {
        if (dialog?.open && addLabel) addLabel.textContent = 'أضف إلى السلة';
      }, 1600);
    } catch (error) {
      if (status) { status.textContent = error.message || 'حصلت مشكلة. جرّب مرة أخرى.'; status.classList.add('is-error'); }
      if (addLabel) addLabel.textContent = 'أضف إلى السلة';
    } finally {
      if (addButton) addButton.disabled = !state?.current?.available;
    }
  }

  root.addEventListener('submit', event => {
    const form = event.target.closest('[data-dr-quick-form]');
    if (!form) return;
    event.preventDefault();
    addFromQuickView(form);
  });

  root.addEventListener('click', event => {
    const button = event.target.closest('[data-roast], [data-blend]');
    if (button) {
      const section = button.closest('[data-dr-taste]');
      if (!section) return;
      const kind = button.hasAttribute('data-roast') ? 'roast' : 'blend';
      section.querySelectorAll('[data-' + kind + ']').forEach(item =>
        item.setAttribute('aria-pressed', String(item === button)));
      updateTaste(section);
      return;
    }
    const quickDialog = event.target.closest('[data-dr-quickview]');
    const mediaButton = event.target.closest('[data-dr-media-id]');
    if (quickDialog && mediaButton) {
      setQuickViewMedia(quickDialog, mediaButton.dataset.drMediaId);
      return;
    }
    const qtyMinus = event.target.closest('[data-dr-qty-minus]');
    const qtyPlus = event.target.closest('[data-dr-qty-plus]');
    if (quickDialog && (qtyMinus || qtyPlus)) {
      const state = getQuickView(quickDialog);
      const input = quickDialog.querySelector('[data-dr-qty-input]');
      const variant = state?.current;
      if (input && variant) {
        const step = Number(variant.increment) || 1;
        const min = Number(variant.min) || 1;
        const max = variant.max == null ? Infinity : Number(variant.max);
        const current = Number(input.value) || min;
        input.value = String(qtyMinus ? Math.max(min, current - step) : Math.min(max, current + step));
        clampQuickViewQuantity(input, variant);
      }
      return;
    }
    const trigger = event.target.closest('[data-dr-open]');
    if (trigger && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      const dialog = document.getElementById(trigger.dataset.drOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        returnFocus.set(dialog, trigger);
        dialog.showModal();
        root.classList.add('dr-modal-open');
        dialog.scrollTop = 0;
        initQuickView(dialog);
        (dialog.querySelector('[data-dr-option-value][aria-pressed="true"]') || dialog.querySelector('[data-dr-add-to-cart]') || dialog.querySelector('[data-dr-close]'))?.focus();
      }
      return;
    }
    const close = event.target.closest('[data-dr-close]');
    if (close) close.closest('dialog')?.close();
    if (event.target.matches('dialog[data-dr-dialog]')) {
      const box = event.target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right ||
          event.clientY < box.top || event.clientY > box.bottom) event.target.close();
    }
    const mobile = event.target.closest('.dr-mobile-panel a');
    if (mobile) mobile.closest('.dr-mobile-nav').open = false;
  });

  root.addEventListener('change', event => {
    if (event.target.matches('[data-dr-qty-input]')) {
      const dialog = event.target.closest('[data-dr-quickview]');
      const state = getQuickView(dialog);
      if (state?.current) clampQuickViewQuantity(event.target, state.current);
      return;
    }
    if (!event.target.matches('[data-dr-variant]')) return;
    const option = event.target.selectedOptions[0];
    const price = event.target.closest('form').querySelector('[data-dr-price]');
    if (!option || !price) return;
    const current = document.createElement('span');
    current.textContent = option.dataset.price;
    price.replaceChildren(current);
    if (option.dataset.compare) {
      const old = document.createElement('del');
      old.textContent = option.dataset.compare;
      price.append(old);
    }
  });

  root.addEventListener('toggle', event => {
    const detail = event.target;
    if (!detail.matches('.dr-faqs details') || !detail.open) return;
    const group = detail.closest('[data-dr-single-open="true"]');
    if (group) group.querySelectorAll('details[open]').forEach(item => {
      if (item !== detail) item.open = false;
    });
  }, true);

  /* close doesn't bubble; capture restores focus after Escape, button or backdrop. */
  root.addEventListener('close', event => {
    if (!event.target.matches('[data-dr-dialog]')) return;
    root.classList.remove('dr-modal-open');
    returnFocus.get(event.target)?.focus();
  }, true);

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      root.querySelectorAll('.dr-mobile-nav[open], .dr-nav-sub[open]').forEach(details => {
        details.open = false;
        details.querySelector('summary')?.focus();
      });
    }
  });
  document.addEventListener('click', event => {
    root.querySelectorAll('.dr-mobile-nav[open], .dr-nav-sub[open]').forEach(details => {
      if (!details.contains(event.target)) details.open = false;
    });
  });
  /* One set of delegated listeners survives Shopify section re-rendering. */
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    root.classList.remove('dr-modal-open');
    for (const [bar, state] of marquees) if (event.target.contains(bar)) state.destroy();
    for (const [bar, state] of announcementBars) if (event.target.contains(bar)) state.destroy();
  });
  document.addEventListener('shopify:block:select', event => {
    const panel = event.target.closest('[data-dr-recommendation]');
    if (panel) {
      const section = panel.closest('[data-dr-taste]');
      const [roast, blend] = panel.dataset.drRecommendation.split('|');
      section.querySelectorAll('[data-roast]').forEach(item =>
        item.setAttribute('aria-pressed', String(item.dataset.roast === roast)));
      section.querySelectorAll('[data-blend]').forEach(item =>
        item.setAttribute('aria-pressed', String(item.dataset.blend === blend)));
      updateTaste(section);
    }
    const details = event.target.closest('.dr-faqs details');
    if (details) details.open = true;
  });
})();
